/**
 * ============================================================
 * LOVEINDIA DISCOUNT PROGRESS
 * ============================================================
 *
 * Actual discount is applied by Kite.
 * This script is DISPLAY ONLY.
 *
 * Discount tiers:
 *
 * 600 lei  -> 5%
 * 1000 lei -> 7.5%
 * 1500 lei -> 10%
 *
 * ============================================================
 */

(() => {

  'use strict';


  /**
   * ============================================================
   * CONFIG
   * ============================================================
   *
   * Change discount thresholds ONLY here.
   */

  const TIERS = [

    {
      amount: 600,
      discount: 5
    },

    {
      amount: 1000,
      discount: 7.5
    },

    {
      amount: 1500,
      discount: 10
    }

  ];


  /**
   * ============================================================
   * CART
   * ============================================================
   */

  async function getCart() {

    const response =
      await fetch(
        window.Shopify.routes.root + 'cart.js',
        {
          headers: {
            'Accept': 'application/json'
          },

          cache: 'no-store'
        }
      );


    if (!response.ok) {

      throw new Error(
        'Could not load cart.'
      );

    }


    return response.json();

  }


  /**
   * ============================================================
   * LANGUAGE
   * ============================================================
   */

  function isRomanian() {

    return (
      document.documentElement.lang
        ?.toLowerCase()
        .startsWith('ro')
    );

  }


  /**
   * ============================================================
   * MONEY
   * ============================================================
   */

  function formatMoney(value) {

    if (isRomanian()) {

      return new Intl.NumberFormat(
        'ro-RO',
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }
      ).format(value) + ' lei';

    }


    return new Intl.NumberFormat(
      'en-US',
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }
    ).format(value) + ' lei';

  }


  /**
   * ============================================================
   * ELIGIBLE CART VALUE
   * ============================================================
   *
   * We use the original merchandise value before
   * Kite applies the tier discount.
   *
   * Free gifts must NOT help the customer reach
   * a discount threshold.
   */

  function getEligibleSubtotal(cart) {

    let subtotal = 0;


    cart.items.forEach(item => {

      const properties =
        item.properties || {};


      /**
       * Spiritual free Book.
       */
      if (
        properties._spiritual_gift === 'true'
      ) {

        return;

      }


      /**
       * Scarves free gift.
       */
      if (
        properties._scarves_gift === 'true'
      ) {

        return;

      }


      /**
       * Shopify values are in bani.
       */
      subtotal +=
        Number(
          item.original_line_price || 0
        );

    });


    return subtotal / 100;

  }


  /**
   * ============================================================
   * TIER STATE
   * ============================================================
   */

  function getTierState(subtotal) {

    let currentTier = null;
    let nextTier = null;


    for (const tier of TIERS) {

      if (
        subtotal >= tier.amount
      ) {

        currentTier = tier;

      } else {

        nextTier = tier;

        break;

      }

    }


    return {
      currentTier,
      nextTier
    };

  }


  /**
   * ============================================================
   * PROGRESS
   * ============================================================
   *
   * Examples:
   *
   * 300 / 600
   * -> 50%
   *
   * 800:
   * current = 600
   * next = 1000
   * -> 50%
   *
   * 1250:
   * current = 1000
   * next = 1500
   * -> 50%
   */

  function getProgress(
    subtotal,
    currentTier,
    nextTier
  ) {

    /**
     * Maximum tier reached.
     */
    if (!nextTier) {

      return 100;

    }


    const start =
      currentTier
        ? currentTier.amount
        : 0;


    const end =
      nextTier.amount;


    const progress =
      (
        (subtotal - start) /
        (end - start)
      ) * 100;


    return Math.max(
      0,
      Math.min(
        100,
        progress
      )
    );

  }


  /**
   * ============================================================
   * TEXT
   * ============================================================
   */

  function getMessage(
    subtotal,
    currentTier,
    nextTier
  ) {

    const ro =
      isRomanian();


    /**
     * Empty cart.
     */
    if (
      subtotal <= 0
    ) {

      const firstTier =
        TIERS[0];


      const amount =
        formatMoney(
          firstTier.amount
        );


      if (ro) {

        return (
          'Adaugă produse în coș și primești ' +
          '<strong>' +
          firstTier.discount +
          '% reducere</strong> ' +
          'de la <strong>' +
          amount +
          '</strong>.'
        );

      }


      return (
        'Add products to your cart and get ' +
        '<strong>' +
        firstTier.discount +
        '% off</strong> ' +
        'from <strong>' +
        amount +
        '</strong>.'
      );

    }


    /**
     * Maximum discount reached.
     */
    if (!nextTier) {

      const maximumTier =
        TIERS[
          TIERS.length - 1
        ];


      if (ro) {

        return (
          'Ai deblocat reducerea maximă de ' +
          '<strong>' +
          maximumTier.discount +
          '%</strong>.'
        );

      }


      return (
        'You have unlocked the maximum ' +
        '<strong>' +
        maximumTier.discount +
        '% discount</strong>.'
      );

    }


    const remaining =
      Math.max(
        0,
        nextTier.amount -
        subtotal
      );


    const amount =
      formatMoney(
        remaining
      );


    /**
     * First threshold not reached yet.
     */
    if (!currentTier) {

      if (ro) {

        return (
          'Mai ai <strong>' +
          amount +
          '</strong> până la reducerea de ' +
          '<strong>' +
          nextTier.discount +
          '%</strong>.'
        );

      }


      return (
        'Add <strong>' +
        amount +
        '</strong> more to unlock ' +
        '<strong>' +
        nextTier.discount +
        '% off</strong>.'
      );

    }


    /**
     * A tier is already active.
     */
    if (ro) {

      return (
        'Ai <strong>' +
        currentTier.discount +
        '% reducere</strong>. ' +
        'Mai ai <strong>' +
        amount +
        '</strong> până la ' +
        '<strong>' +
        nextTier.discount +
        '%</strong>.'
      );

    }


    return (
      'You have <strong>' +
      currentTier.discount +
      '% off</strong>. ' +
      'Add <strong>' +
      amount +
      '</strong> more to unlock ' +
      '<strong>' +
      nextTier.discount +
      '% off</strong>.'
    );

  }


  /**
   * ============================================================
   * RENDER
   * ============================================================
   */

  async function updateWidgets() {

    const widgets =
      document.querySelectorAll(
        '[data-aw-discount-progress]'
      );


    if (
      widgets.length === 0
    ) {

      return;

    }


    try {

      const cart =
        await getCart();


      const subtotal =
        getEligibleSubtotal(
          cart
        );


      const {
        currentTier,
        nextTier
      } =
        getTierState(
          subtotal
        );


      const progress =
        subtotal <= 0

          ? 0

          : getProgress(
              subtotal,
              currentTier,
              nextTier
            );


      const message =
        getMessage(
          subtotal,
          currentTier,
          nextTier
        );


      widgets.forEach(widget => {

        /**
         * Widget is now also visible
         * when cart is empty.
         */
        widget.hidden =
          false;


        const messageElement =
          widget.querySelector(
            '[data-aw-discount-message]'
          );


        const bar =
          widget.querySelector(
            '[data-aw-discount-bar]'
          );


        if (messageElement) {

          messageElement.innerHTML =
            message;

        }


        if (bar) {

          bar.style.width =
            progress + '%';

        }

      });


    } catch (error) {

      console.error(
        'Discount progress error:',
        error
      );

    }

  }


  /**
   * ============================================================
   * INITIAL LOAD
   * ============================================================
   */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      updateWidgets
    );

  } else {

    updateWidgets();

  }


  /**
   * ============================================================
   * ETHEREAL / LIQUID AJAX CART
   * ============================================================
   *
   * Refresh after:
   *
   * - add product
   * - remove product
   * - quantity +
   * - quantity -
   * - Spiritual gift
   * - Scarves gift
   */

  document.addEventListener(
    'liquid-ajax-cart:request-end',
    () => {

      updateWidgets();

    }
  );


})();